/**
 * AI 보고서를 **문서처럼** 그린다
 * ---------------------------------------------------------------------------
 * ## 왜 만들었나
 * 전에는 돌아온 글을 `white-space: pre-wrap` 으로 그대로 흘렸다. AI 는
 * 마크다운으로 쓰는데(제목 `#`, 굵게 `**`, 그리고 **표**), 그걸 글자
 * 그대로 보여 주니 화면에 `| 날짜 | 가동시간 |` 같은 작대기가 그대로
 * 떴다. 표가 가장 쓸모 있는 부분인데 가장 읽기 어려웠다.
 *
 * 받아서 윗선에 올리는 글이다. 문서로 보여야 한다.
 *
 * ## 왜 직접 파싱하지 않았나
 * 모델이 내놓는 마크다운은 우리가 통제하지 못한다 — 표 안에 굵게가
 * 섞이고, 칸 수가 어긋나고, 코드블록이 끼기도 한다. 손으로 짠 파서는
 * 그런 날 깨지고, 깨진 티도 안 난다. 표준 파서(remark-gfm)를 쓴다.
 * WORK&CRM 도 react-markdown 을 쓰고 있어 플랫폼 안에서 같은 도구다.
 *
 * ## 인쇄
 * 담당자는 이 글을 출력해 결재에 붙인다. `@media print` 로 표가 페이지
 * 중간에 잘리지 않게 하고, 화면용 배경색은 지운다.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export function ReportDocument({ body }: { body: string }) {
  return (
    <div className="eco-report">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        /*
          표가 넓어지면 가로로만 밀리게 감싼다. 감싸지 않으면 팝업 전체가
          옆으로 늘어나 닫기 단추까지 화면 밖으로 나간다.
        */
        components={{
          table: ({ children }) => (
            <div className="eco-report-table-wrap">
              <table>{children}</table>
            </div>
          ),
          /*
            숫자 칸은 오른쪽으로 민다. 자릿수가 세로로 맞아야 눈으로
            크기를 비교할 수 있다 — 가동시간·농도·발생량이 왼쪽 정렬이면
            표가 있어도 읽히지 않는다.

            모델에게 정렬을 지시하지 않는다(지켜 줄 거라는 보장이 없다).
            칸에 **담긴 값**을 보고 정한다.
          */
          td: ({ children, ...rest }) => (
            <td {...rest} className={isNumeric(children) ? 'num' : undefined}>
              {children}
            </td>
          ),
        }}
      >
        {escapeTildes(body)}
      </ReactMarkdown>
      <style>{CSS}</style>
    </div>
  );
}

/**
 * 물결표(`~`)를 **글자로 되돌린다.**
 *
 * ## 왜 필요한가
 * GFM 은 `~글자~` 를 취소선으로 읽는다. 그런데 한국어 보고서에서 `~` 는
 * **기간을 잇는 글자**다. 총평에 「10-23~10-25 … 10-26~10-29」 처럼 두 번
 * 나오자 그 사이 한 문단이 통째로 그어졌다 — 읽는 사람에게는 *취소된 내용*
 * 으로 보인다. 보고서에서 가장 나쁜 오해다.
 *
 * 모델에게 "물결표를 쓰지 말라"고 시키는 방법도 있지만, 지켜 줄 보장이 없고
 * 한국어 기간 표기를 금지하는 것도 이상하다. **그릴 때 아예 취소선이 생길 수
 * 없게** 막는다 — 보고서에 취소선이 쓰일 일은 없다.
 *
 * 코드 안(`` ` `` · ```` ``` ````)은 건드리지 않는다. 거기서는 `\~` 가 글자
 * 그대로 보여서 오히려 이상해진다.
 */
function escapeTildes(md: string): string {
  return md
    .split(/(```[\s\S]*?```|`[^`\n]*`)/g)
    .map((part, i) => (i % 2 === 1 ? part : part.replace(/~/g, '\\~')))
    .join('');
}

/**
 * 이 칸이 숫자인가.
 *
 * 「12.5」 「1,240」 「3.2%」 「-」 「7.1℃」 처럼 수와 단위·기호만 있는
 * 값을 숫자로 본다. 「확인 필요」 같은 글은 왼쪽에 둔다.
 *
 * 빈 칸과 「-」 도 숫자 쪽에 붙인다. 숫자 줄 사이에 혼자 왼쪽으로
 * 붙어 있으면 표가 어긋나 보인다.
 */
function isNumeric(children: unknown): boolean {
  const text = flatten(children).trim();
  if (text === '') return true;
  return /^[-+]?[\d,]*\.?\d*\s*(%|℃|도|배|시간|분|일|건|회|kg|g|t|㎥|m3|L|㎎\/L|mg\/L|ppm|kWh|원)?$/i.test(
    text,
  );
}

/** React 노드에서 글자만 긁어낸다. 표 칸에는 <strong> 이 섞여 들어온다. */
function flatten(node: unknown): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(flatten).join('');
  if (typeof node === 'object' && 'props' in (node as Record<string, unknown>)) {
    const props = (node as { props?: { children?: unknown } }).props;
    return flatten(props?.children);
  }
  return '';
}

/*
 * 글꼴 크기와 여백은 **보고 문서**의 비율을 따른다 — 제목이 본문보다
 * 확실히 크고, 문단 사이가 줄 사이보다 넓어야 눈이 쉬어 간다.
 */
const CSS = `
.eco-report {
  font-size: 14px;
  line-height: 1.75;
  color: #1f1f1f;
  word-break: break-word;
}
.eco-report > :first-child { margin-top: 0; }
.eco-report > :last-child { margin-bottom: 0; }

.eco-report h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #1f1f1f;
  letter-spacing: -0.3px;
}
.eco-report h2 {
  font-size: 16px;
  font-weight: 700;
  margin: 26px 0 10px;
  padding-left: 9px;
  border-left: 4px solid #1677ff;
}
.eco-report h3 {
  font-size: 14px;
  font-weight: 700;
  margin: 18px 0 8px;
  color: #262626;
}
.eco-report p { margin: 0 0 10px; }
.eco-report strong { font-weight: 700; color: #000; }

.eco-report ul, .eco-report ol { margin: 0 0 12px; padding-left: 20px; }
.eco-report li { margin: 3px 0; }
.eco-report li::marker { color: #8c8c8c; }

.eco-report hr {
  border: 0;
  border-top: 1px solid #f0f0f0;
  margin: 20px 0;
}

/* ---- 표 — 여기가 핵심이다 ---- */
.eco-report-table-wrap {
  overflow-x: auto;
  margin: 10px 0 18px;
}
.eco-report table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
  /* 칸이 많아도 글자가 세로로 쌓이지 않게 */
  min-width: max-content;
}
.eco-report thead th {
  background: #fafafa;
  font-weight: 700;
  text-align: left;
  white-space: nowrap;
  border-top: 1.5px solid #595959;
  border-bottom: 1.5px solid #595959;
  padding: 8px 12px;
}
.eco-report tbody td {
  border-bottom: 1px solid #f0f0f0;
  padding: 7px 12px;
  vertical-align: top;
}
.eco-report tbody tr:last-child td { border-bottom: 1.5px solid #595959; }
/*
  숫자 칸은 오른쪽으로. 자릿수가 맞아야 눈으로 크기를 비교할 수 있다.
  내용이 숫자·기호뿐인 칸만 골라낸다 — 서식 지시 없이 결과로 판단한다.
*/
.eco-report tbody td.num { text-align: right; font-variant-numeric: tabular-nums; }

.eco-report blockquote {
  margin: 10px 0;
  padding: 8px 14px;
  background: #fffbe6;
  border-left: 3px solid #faad14;
  color: #613400;
}
.eco-report code {
  background: #f5f5f5;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

@media print {
  .eco-report { font-size: 11pt; color: #000; }
  .eco-report h2 { border-left-color: #000; }
  .eco-report thead th { background: transparent; }
  /* 표와 제목이 페이지 경계에서 잘리지 않게 */
  .eco-report table, .eco-report tr { page-break-inside: avoid; }
  .eco-report h1, .eco-report h2, .eco-report h3 { page-break-after: avoid; }
  .eco-report-table-wrap { overflow: visible; }
}
`;
